import { useSyncExternalStore } from 'react';

const LATEST_EDITING_PATIENT_ID_KEY = 'latest_editing_patient_id';
const CHANGE_EVENT = 'latest-editing-patient-change';

// localStorage writes in the same tab fire no `storage` event, so readers (the sidebar menu)
// would keep links built from the previous patient (HIT-14575). Notify them explicitly.
const notifyChange = () => {
  window.dispatchEvent(new Event(CHANGE_EVENT));
};

const getStorageKey = (tenant?: string, clinicNumber?: string) =>
  tenant && clinicNumber
    ? `${LATEST_EDITING_PATIENT_ID_KEY}:${tenant}:${clinicNumber}`
    : tenant
      ? `${LATEST_EDITING_PATIENT_ID_KEY}:${tenant}`
      : LATEST_EDITING_PATIENT_ID_KEY;

export const setLatestEditingPatientId = (
  tenant: string | undefined,
  patientId: number | string,
  clinicNumber?: string
) => {
  if (typeof window === 'undefined') {
    return;
  }

  const normalizedId = Number(patientId);
  if (!Number.isInteger(normalizedId) || normalizedId <= 0) {
    return;
  }

  const key = getStorageKey(tenant, clinicNumber);
  if (window.localStorage.getItem(key) === String(normalizedId)) {
    return;
  }
  window.localStorage.setItem(key, String(normalizedId));
  notifyChange();
};

export const getLatestEditingPatientId = (tenant?: string, clinicNumber?: string): number | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const value = window.localStorage.getItem(getStorageKey(tenant, clinicNumber));
  if (!value) {
    return null;
  }

  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    return null;
  }

  return parsed;
};

export const clearLatestEditingPatientId = (tenant?: string, clinicNumber?: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(getStorageKey(tenant, clinicNumber));
  notifyChange();
};

const subscribe = (onChange: () => void) => {
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
};

/** Latest editing patient id that re-renders the caller whenever it changes. */
export const useLatestEditingPatientId = (tenant?: string, clinicNumber?: string): number | null =>
  useSyncExternalStore(
    subscribe,
    () => getLatestEditingPatientId(tenant, clinicNumber),
    () => null
  );
